/**
 * =============================================================================
 * PHS ENTERPRISE HELPDESK VIEW
 * =============================================================================
 * Main entry point for the enterprise command centre.
 * Wraps existing data/logic with premium enterprise UI.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { withWyldecrestAuth } from './WyldecrestAuthGuard';
import { useRealtime, mergeJobs, eventToToast } from '@/hooks/useRealtime';
import { useHelpdeskUrlState } from '@/hooks/useUrlState';

// Enterprise Components
import { 
  PHSCommandLayout,
  CommandCentreDashboard,
  TOKENS 
} from '@/components/phs/enterprise';
import EnterpriseJobWorkspace from '@/components/phs/enterprise/EnterpriseJobWorkspace';
import ErrorBoundary from '@/components/ErrorBoundary';

// UI Components for New Job Modal
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Upload, X, Loader2, Building2, Download, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function EnterpriseHelpdeskView({ wyldecrestUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  
  // URL-driven state for filters
  const { params: urlState, updateParams: updateUrlState, getParam } = useHelpdeskUrlState();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(params.jobId || null);
  const [notifications, setNotifications] = useState([]);
  
  // New Job Modal State
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [newJobSubmitting, setNewJobSubmitting] = useState(false);
  const [parks, setParks] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [parkSearch, setParkSearch] = useState('');
  const [showParkDropdown, setShowParkDropdown] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newJob, setNewJob] = useState({
    park_id: '',
    park_name: '',
    postcode: '',
    plot: '',
    category: '',
    priority: 'Medium',
    description: '',
    contact_name: '',
    contact_phone: '',
    contractor_id: '',
    contractor_name: '',
  });

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Handle action=new-job query parameter
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new-job') {
      setShowNewJobModal(true);
      // Clear the action from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Fetch parks for dropdown
  useEffect(() => {
    const fetchParks = async () => {
      try {
        const res = await axios.get(`${API}/api/wyldecrest/parks`, { headers: getAuthHeaders() });
        setParks(res.data?.parks || res.data || []);
      } catch (err) {
        console.error('Error fetching parks:', err);
        // Try alternate endpoint
        try {
          const res2 = await axios.get(`${API}/api/parks`, { headers: getAuthHeaders() });
          setParks(res2.data?.parks || res2.data || []);
        } catch (err2) {
          console.error('Error fetching parks (fallback):', err2);
        }
      }
    };
    fetchParks();
  }, []);

  // Fetch contractors for dropdown
  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const res = await axios.get(`${API}/api/contractors`, { headers: getAuthHeaders() });
        setContractors(res.data || []);
      } catch (err) {
        console.error('Error fetching contractors:', err);
      }
    };
    fetchContractors();
  }, []);

  // Realtime connection
  const { isConnected, reconnect, lastEvent } = useRealtime('wyldecrest_helpdesk', {
    onEvent: (event) => {
      // Handle real-time updates
      if (event.type === 'job_update') {
        setJobs(prev => mergeJobs(prev, event.job));
      }
      // Add to notifications
      setNotifications(prev => [{
        message: eventToToast(event)?.title || 'New update',
        time: 'Just now',
        read: false
      }, ...prev].slice(0, 10));
    }
  });

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/wyldecrest/jobs`, {
        params: { limit: 200 },
        headers: getAuthHeaders()
      });
      setJobs(res.data?.jobs || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Update selected job when params change
  useEffect(() => {
    if (params.jobId) {
      setSelectedJobId(params.jobId);
    } else {
      setSelectedJobId(null);
    }
  }, [params.jobId]);

  // URL-driven filtered jobs - CRITICAL for workspace continuity
  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    
    // Status filter (e.g., ?status=Awaiting%20Quote)
    const statusFilter = getParam('status');
    if (statusFilter) {
      result = result.filter(j => 
        j.status?.toLowerCase() === statusFilter.toLowerCase() ||
        j.status?.toLowerCase().replace(/\s+/g, '-') === statusFilter.toLowerCase().replace(/\s+/g, '-')
      );
    }
    
    // Status list filter (e.g., ?status_list=Quote%20Awaiting%20Review,Quote%20Queried)
    const statusListFilter = getParam('status_list');
    if (statusListFilter) {
      const statuses = statusListFilter.split(',').map(s => s.trim().toLowerCase());
      result = result.filter(j => 
        statuses.some(s => j.status?.toLowerCase() === s || j.status?.toLowerCase().includes(s))
      );
    }
    
    // Unassigned filter (e.g., ?unassigned=true)
    const unassignedFilter = getParam('unassigned');
    if (unassignedFilter === 'true') {
      result = result.filter(j => !j.contractor_id && !j.contractor_name);
    }
    
    // SLA filter (e.g., ?sla=breached)
    const slaFilter = getParam('sla');
    if (slaFilter === 'breached') {
      result = result.filter(j => j.sla_breached || (j.sla_due_at && new Date(j.sla_due_at) < new Date()));
    } else if (slaFilter === 'at_risk') {
      result = result.filter(j => j.sla_risk);
    }
    
    // Follow-on filter (e.g., ?followon=required)
    const followonFilter = getParam('followon');
    if (followonFilter === 'required') {
      result = result.filter(j => j.follow_on_required || j.requires_follow_on || j.is_follow_on);
    }
    
    // Filter param (legacy support for quick filters like ?filter=overdue)
    const quickFilter = getParam('filter');
    if (quickFilter) {
      switch (quickFilter.toLowerCase()) {
        case 'overdue':
        case 'sla_breached':
          result = result.filter(j => j.sla_due_at && new Date(j.sla_due_at) < new Date());
          break;
        case 'due_today':
          const today = new Date().toDateString();
          result = result.filter(j => j.sla_due_at && new Date(j.sla_due_at).toDateString() === today);
          break;
        case 'unassigned':
          result = result.filter(j => !j.contractor_id && j.status !== 'cancelled');
          break;
        case 'high_priority':
          result = result.filter(j => j.priority?.toLowerCase() === 'high' || j.priority?.toLowerCase() === 'emergency');
          break;
        case 'follow_on':
          result = result.filter(j => j.is_follow_on);
          break;
        default:
          break;
      }
    }
    
    // Priority filter
    const priorityFilter = getParam('priority');
    if (priorityFilter) {
      result = result.filter(j => j.priority?.toLowerCase() === priorityFilter.toLowerCase());
    }
    
    // Search filter
    const searchFilter = getParam('search');
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      result = result.filter(j =>
        j.job_reference?.toLowerCase().includes(q) ||
        j.description?.toLowerCase().includes(q) ||
        j.site_name?.toLowerCase().includes(q) ||
        j.category?.toLowerCase().includes(q) ||
        j.contractor_name?.toLowerCase().includes(q)
      );
    }
    
    // Contractor filter
    const contractorFilter = getParam('contractor');
    if (contractorFilter) {
      result = result.filter(j => j.contractor_id === contractorFilter);
    }
    
    // Park filter
    const parkFilter = getParam('park');
    if (parkFilter) {
      result = result.filter(j => j.park_id === parkFilter || j.site_name?.toLowerCase().includes(parkFilter.toLowerCase()));
    }
    
    return result;
  }, [jobs, getParam]);

  // Calculate job counts for sidebar
  const jobCounts = useMemo(() => {
    const counts = {
      total: jobs.length,
      pipeline: jobs.filter(j => !['completed', 'paid', 'cancelled'].includes(j.status?.toLowerCase())).length,
      unassigned: jobs.filter(j => !j.contractor_id && j.status !== 'cancelled').length,
      follow_on: jobs.filter(j => j.is_follow_on).length,
      sla_risk: jobs.filter(j => j.sla_status === 'at_risk' || j.sla_status === 'overdue').length,
      awaiting_invoice: jobs.filter(j => j.status?.toLowerCase() === 'awaiting_invoice').length,
      invoiced: jobs.filter(j => j.status?.toLowerCase() === 'invoiced').length,
      tasks: 0,
      alerts: jobs.filter(j => j.sla_status === 'overdue').length,
    };
    return counts;
  }, [jobs]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('wyldecrest_token');
    localStorage.removeItem('wyldecrest_user');
    navigate('/admin/login');
  };

  // Handle New Job submission
  const handleNewJobSubmit = async (e) => {
    e.preventDefault();
    if (!newJob.park_name.trim()) {
      toast.error('Please select a park/site');
      return;
    }
    if (!newJob.description.trim() || newJob.description.trim().length < 10) {
      toast.error('Please provide a description (at least 10 characters)');
      return;
    }
    
    // Track click event
    Sentry.addBreadcrumb({
      category: 'job',
      message: 'New Job submission started',
      level: 'info',
    });
    
    setNewJobSubmitting(true);
    try {
      const payload = {
        site_name: newJob.park_name,
        park_id: newJob.park_id || null,
        park_name: newJob.park_name,
        postcode: newJob.postcode || 'N/A',
        plot_number: newJob.plot || null,
        job_type: newJob.category || 'PLUMBING',
        category: newJob.category || 'PLUMBING',
        priority: newJob.priority || 'Medium',
        description: newJob.description,
        client_contact_name: newJob.contact_name || null,
        client_contact_phone: newJob.contact_phone || null,
        // Contractor assignment
        contractor_id: newJob.contractor_id || null,
        contractor_name: newJob.contractor_name || null,
        // Set initial status based on contractor assignment
        status: newJob.contractor_id ? 'Assigned' : 'Unassigned',
      };
      
      const res = await axios.post(`${API}/api/fm/jobs`, payload, { headers: getAuthHeaders() });
      
      if (res.data?.job) {
        const jobRef = res.data.job.job_reference || res.data.job.id;
        const jobId = res.data.job.id;
        
        // Upload any attached files as evidence
        if (uploadedFiles.length > 0) {
          try {
            const formData = new FormData();
            uploadedFiles.forEach(f => formData.append('files', f.file));
            formData.append('stage', 'before');
            
            await axios.post(
              `${API}/api/fm/jobs/${jobId}/upload-evidence`,
              formData,
              { 
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
              }
            );
          } catch (uploadErr) {
            console.error('Failed to upload initial files:', uploadErr);
            toast.error('Job created but file upload failed');
          }
        }
        
        toast.success(`Job ${jobRef} created successfully${newJob.contractor_id ? ' and assigned' : ''}`);
        
        // Track success
        Sentry.addBreadcrumb({
          category: 'job',
          message: `New Job created: ${jobRef}`,
          level: 'info',
        });
        
        setShowNewJobModal(false);
        setUploadedFiles([]);
        setNewJob({
          park_id: '',
          park_name: '',
          postcode: '',
          plot: '',
          category: '',
          priority: 'Medium',
          description: '',
          contact_name: '',
          contact_phone: '',
          contractor_id: '',
          contractor_name: '',
        });
        setParkSearch('');
        
        // Refresh jobs list
        fetchJobs();
        
        // Navigate to the new job
        navigate(`/phs/helpdesk/job/${jobId}`);
      }
    } catch (err) {
      console.error('Error creating job:', err);
      
      // Track failure
      Sentry.captureException(err, {
        tags: { action: 'newJobSubmit' },
        extra: { parkName: newJob.park_name, category: newJob.category }
      });
      
      // Better error message display
      let errorMsg = 'Failed to create job';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors
          const firstError = err.response.data.detail[0];
          errorMsg = firstError?.msg || 'Validation error';
          if (firstError?.loc) {
            const field = firstError.loc[firstError.loc.length - 1];
            errorMsg = `${field}: ${errorMsg}`;
          }
        }
      }
      toast.error(errorMsg);
    } finally {
      setNewJobSubmitting(false);
    }
  };

  // Filter parks for dropdown - improved auto-search
  const filteredParks = useMemo(() => {
    if (!parkSearch || parkSearch.length < 1) return parks.slice(0, 15);
    const search = parkSearch.toLowerCase().trim();
    return parks.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.postcode?.toLowerCase().includes(search) ||
      p.region?.toLowerCase().includes(search)
    ).slice(0, 15);
  }, [parks, parkSearch]);

  // Filter contractors by trade if category is selected
  const filteredContractors = useMemo(() => {
    if (!newJob.category) return contractors;
    // Map category to trade
    const tradeMap = {
      'PLUMBING': ['Plumber', 'Plumbing'],
      'ELECTRICAL': ['Electrician', 'Electrical'],
      'ROOFING': ['Roofer', 'Roofing'],
      'GLAZING': ['Glazier', 'Glazing'],
      'GENERAL_BUILDING': ['General Builder', 'General Building', 'Builder'],
      'BOILER_SERVICE': ['Gas Engineer', 'Boiler', 'Heating'],
      'DRAIN_UNBLOCK': ['Drainage', 'Plumber'],
      'MAINS_GAS_CHECK': ['Gas Engineer', 'Gas'],
      'LPG_GAS_CHECK': ['Gas Engineer', 'LPG', 'Gas'],
      'EICR_PARK_HOME': ['Electrician', 'EICR'],
      'COMPLIANCE': ['Compliance', 'General'],
      'PPM_INSPECTION': ['General', 'Inspector'],
      'FIRE_ALARM': ['Fire Safety', 'Electrician'],
    };
    const trades = tradeMap[newJob.category] || [];
    if (trades.length === 0) return contractors;
    return contractors.filter(c => 
      trades.some(t => c.trade?.toLowerCase().includes(t.toLowerCase()))
    );
  }, [contractors, newJob.category]);

  // Render content based on route
  const renderContent = () => {
    // If we have a job ID in params, show job workspace
    if (selectedJobId) {
      return (
        <EnterpriseJobWorkspace 
          jobId={selectedJobId}
          onBack={() => {
            setSelectedJobId(null);
            navigate('/phs/helpdesk');
          }}
          wyldecrestUser={wyldecrestUser}
        />
      );
    }

    // Check if there are active URL filters
    const hasFilters = getParam('status') || getParam('status_list') || getParam('filter') || getParam('search') || getParam('priority') || getParam('sla') || getParam('unassigned') || getParam('followon');
    
    // If there are filters, show filtered jobs table view
    if (hasFilters) {
      const filterLabel = getParam('status') || getParam('filter') || 'Filtered Jobs';
      
      return (
        <div className="p-6">
          {/* Filtered Jobs Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{filterLabel}</h1>
              <p className="text-slate-500">
                Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Export CSV Button */}
              <button
                onClick={() => {
                  // Export filtered jobs to CSV
                  const headers = ['Job Ref', 'Status', 'Priority', 'Park', 'Category', 'Contractor', 'Created', 'SLA'];
                  const rows = filteredJobs.map(j => [
                    j.job_reference || j.id,
                    j.status,
                    j.priority,
                    j.site_name || j.park_name,
                    j.category,
                    j.contractor_name || 'Unassigned',
                    new Date(j.created_at).toLocaleDateString(),
                    j.sla_due_at ? new Date(j.sla_due_at).toLocaleDateString() : '-'
                  ]);
                  const csv = [headers, ...rows].map(r => r.map(c => `"${c || ''}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${filterLabel.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  toast.success('CSV exported');
                }}
                className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => navigate('/phs/helpdesk')}
                className="px-3 py-2 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 flex items-center gap-1"
              >
                Clear filters
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          
          {/* Filtered Jobs Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Job Ref</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Priority</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Park</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Category</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Contractor</th>
                  <th className="text-left p-4 font-semibold text-slate-700">SLA</th>
                  <th className="text-left p-4 font-semibold text-slate-700 w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      No jobs found matching this filter
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map(job => {
                    const slaBreached = job.sla_due_at && new Date(job.sla_due_at) < new Date();
                    return (
                      <tr 
                        key={job.id} 
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/phs/helpdesk/job/${job.id}`)}
                        data-testid={`filtered-job-row-${job.id}`}
                      >
                        <td className="p-4">
                          <span className="font-mono font-medium text-teal-600">
                            {job.job_reference || job.id?.slice(0, 8)}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {job.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            job.priority === 'Emergency' ? 'bg-red-100 text-red-700' :
                            job.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                            job.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {job.priority || 'Medium'}
                          </span>
                        </td>
                        <td className="p-4 max-w-[200px] truncate">{job.site_name || job.park_name}</td>
                        <td className="p-4">{job.category || '-'}</td>
                        <td className="p-4">
                          {job.contractor_name || (
                            <span className="text-amber-600 text-xs font-medium">Unassigned</span>
                          )}
                        </td>
                        <td className="p-4">
                          {job.sla_due_at ? (
                            <span className={`text-xs ${slaBreached ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                              {slaBreached ? 'BREACHED' : new Date(job.sla_due_at).toLocaleDateString()}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/phs/helpdesk/job/${job.id}`)}
                            className="p-1.5 hover:bg-teal-50 rounded text-teal-600"
                            title="Open Job"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Otherwise show command centre dashboard
    return (
      <div className="p-6">
        {/* Helpdesk Dashboard Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Helpdesk Dashboard</h1>
            <p className="text-slate-500">Live FM operations board - What needs attention now?</p>
          </div>
        </div>
        
        <CommandCentreDashboard 
          jobs={filteredJobs}
          loading={loading}
          onJobClick={(job) => navigate(`/phs/helpdesk/job/${job.id}`)}
          onRefresh={fetchJobs}
        />
      </div>
    );
  };

  return (
    <PHSCommandLayout
      user={wyldecrestUser}
      onLogout={handleLogout}
      jobCounts={jobCounts}
      notifications={notifications}
    >
      <ErrorBoundary componentName="EnterpriseHelpdesk">
        {renderContent()}
      </ErrorBoundary>
      
      {/* New Job Modal */}
      <Dialog open={showNewJobModal} onOpenChange={(open) => {
        setShowNewJobModal(open);
        if (!open) {
          setParkSearch('');
          setShowParkDropdown(false);
          setUploadedFiles([]);
          setNewJob({
            park_id: '',
            park_name: '',
            postcode: '',
            plot: '',
            category: '',
            priority: 'Medium',
            description: '',
            contact_name: '',
            contact_phone: '',
            contractor_id: '',
            contractor_name: '',
          });
        }
      }}>
        <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="new-job-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Plus className="w-5 h-5" />
              Log New Job
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleNewJobSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {/* Park Autocomplete */}
              <div className="relative">
                <label className="text-sm text-slate-600 mb-1 block">Park / Site *</label>
                <Input
                  placeholder="Search parks..."
                  value={parkSearch}
                  onChange={(e) => {
                    setParkSearch(e.target.value);
                    setShowParkDropdown(true);
                    // Also update park_name for manual entry
                    setNewJob(prev => ({...prev, park_name: e.target.value}));
                  }}
                  onFocus={() => setShowParkDropdown(true)}
                  onBlur={() => {
                    // Close dropdown after a small delay to allow click
                    setTimeout(() => setShowParkDropdown(false), 200);
                  }}
                  className="rounded-xl border-slate-200 focus:border-teal-500"
                  data-testid="park-search-input"
                />
                {showParkDropdown && parkSearch && (
                  <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-xl mt-1 max-h-48 overflow-y-auto shadow-lg">
                    {filteredParks.map(park => (
                      <button
                        key={park.id}
                        type="button"
                        onClick={() => {
                          setNewJob({
                            ...newJob,
                            park_id: park.id,
                            park_name: park.name,
                            postcode: park.postcode || ''
                          });
                          setParkSearch(park.name);
                          setShowParkDropdown(false);
                        }}
                        className="w-full text-left p-3 hover:bg-teal-50 border-b last:border-0"
                      >
                        <div className="font-medium text-sm">{park.name}</div>
                        {park.postcode && <div className="text-xs text-slate-500">{park.postcode}</div>}
                      </button>
                    ))}
                    {filteredParks.length === 0 && (
                      <div className="p-3 text-sm text-slate-500">No parks found</div>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Plot</label>
                <Input
                  placeholder="Plot number"
                  value={newJob.plot}
                  onChange={(e) => setNewJob({...newJob, plot: e.target.value})}
                  className="rounded-xl border-slate-200 focus:border-teal-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Category</label>
                <Select value={newJob.category} onValueChange={(v) => setNewJob({...newJob, category: v})}>
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PLUMBING">Plumbing</SelectItem>
                    <SelectItem value="ELECTRICAL">Electrical</SelectItem>
                    <SelectItem value="ROOFING">Roofing</SelectItem>
                    <SelectItem value="GLAZING">Glazing</SelectItem>
                    <SelectItem value="GENERAL_BUILDING">General Building</SelectItem>
                    <SelectItem value="BOILER_SERVICE">Boiler Service</SelectItem>
                    <SelectItem value="DRAIN_UNBLOCK">Drain Unblock</SelectItem>
                    <SelectItem value="MAINS_GAS_CHECK">Gas Check (Mains)</SelectItem>
                    <SelectItem value="LPG_GAS_CHECK">Gas Check (LPG)</SelectItem>
                    <SelectItem value="EICR_PARK_HOME">EICR</SelectItem>
                    <SelectItem value="COMPLIANCE">Compliance</SelectItem>
                    <SelectItem value="PPM_INSPECTION">PPM Inspection</SelectItem>
                    <SelectItem value="FIRE_ALARM">Fire Alarm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Priority</label>
                <Select value={newJob.priority} onValueChange={(v) => setNewJob({...newJob, priority: v})}>
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Emergency">Emergency</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Description *</label>
              <Textarea
                placeholder="Describe the issue..."
                value={newJob.description}
                onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                className="rounded-xl border-slate-200 focus:border-teal-500 min-h-[100px]"
                data-testid="job-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Contact Name</label>
                <Input
                  placeholder="Site contact"
                  value={newJob.contact_name}
                  onChange={(e) => setNewJob({...newJob, contact_name: e.target.value})}
                  className="rounded-xl border-slate-200 focus:border-teal-500"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Contact Phone</label>
                <Input
                  placeholder="Phone number"
                  value={newJob.contact_phone}
                  onChange={(e) => setNewJob({...newJob, contact_phone: e.target.value})}
                  className="rounded-xl border-slate-200 focus:border-teal-500"
                />
              </div>
            </div>
            
            {/* Assign Contractor Section */}
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Assign Contractor (Optional)</label>
              <Select 
                value={newJob.contractor_id || "unassigned"} 
                onValueChange={(v) => {
                  if (v === "unassigned") {
                    setNewJob({
                      ...newJob, 
                      contractor_id: '',
                      contractor_name: ''
                    });
                  } else {
                    const contractor = contractors.find(c => c.id === v);
                    setNewJob({
                      ...newJob, 
                      contractor_id: v,
                      contractor_name: contractor?.company_name || contractor?.name || ''
                    });
                  }
                }}
              >
                <SelectTrigger className="rounded-xl border-slate-200">
                  <SelectValue placeholder="Select contractor (or leave unassigned)" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="unassigned">Leave Unassigned</SelectItem>
                  {filteredContractors.map(contractor => (
                    <SelectItem key={contractor.id} value={contractor.id}>
                      <div className="flex items-center gap-2">
                        <span>{contractor.company_name || contractor.name}</span>
                        <span className="text-xs text-slate-400">({contractor.trade})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newJob.category && filteredContractors.length < contractors.length && (
                <p className="text-xs text-slate-400 mt-1">
                  Showing contractors matching "{newJob.category}" trade
                </p>
              )}
            </div>
            
            {/* Upload Images/Documents Section */}
            <div>
              <label className="text-sm text-slate-600 mb-1 block">Upload Images / Documents (Optional)</label>
              <div 
                className={`border-2 border-dashed rounded-xl p-4 text-center transition ${
                  uploading ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:border-teal-300'
                }`}
              >
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => {
                    const files = [...e.target.files];
                    if (files.length > 0) {
                      setUploadedFiles(prev => [...prev, ...files.map(f => ({
                        file: f,
                        name: f.name,
                        size: f.size,
                        type: f.type,
                        preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null
                      }))]);
                    }
                  }}
                  className="hidden"
                  id="new-job-file-upload"
                />
                <label htmlFor="new-job-file-upload" className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-teal-600 animate-spin mx-auto mb-1" />
                  ) : (
                    <Upload className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                  )}
                  <p className="text-xs text-slate-500">
                    Click to upload photos or documents
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Before photos, site images, documents
                  </p>
                </label>
              </div>
              
              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div className="mt-2 space-y-2">
                  {uploadedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      {file.preview ? (
                        <img src={file.preview} alt="" className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-slate-200 rounded flex items-center justify-center">
                          <Download className="w-4 h-4 text-slate-400" />
                        </div>
                      )}
                      <span className="text-xs text-slate-600 flex-1 truncate">{file.name}</span>
                      <span className="text-[10px] text-slate-400">{(file.size / 1024).toFixed(0)}KB</span>
                      <button
                        type="button"
                        onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1 hover:bg-slate-200 rounded"
                      >
                        <X className="w-3 h-3 text-slate-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewJobModal(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={newJobSubmitting || !newJob.park_name.trim() || !newJob.description.trim()}
                className="rounded-xl bg-teal-600 hover:bg-teal-700"
                data-testid="submit-new-job-btn"
              >
                {newJobSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Job
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PHSCommandLayout>
  );
}

// Export with auth guard
export default withWyldecrestAuth(EnterpriseHelpdeskView, 'helpdesk');
